import { type RefObject, useEffect, useRef } from 'react';
import {
  type Point,
  type ViewTransform,
  IDENTITY_VIEW,
  distance,
  midpoint,
  panBy,
  toTransformString,
  zoomAround,
} from './panZoomMath';

/**
 * Block 14 «Раскрой» — зум/пан карты раскроя (B-246/CUT-5.2b, ADR-0088).
 * Кастомный Pointer Events-слой (без либ), по паттерну usePinchZoom.
 *
 * **Модель:** состояние вида `{scale, tx, ty}` в ref'ах (без ре-рендера на
 * кадр), в `requestAnimationFrame` пишется `transform="translate(tx ty)
 * scale(scale)"` на ВНУТРЕННЮЮ `<g class="cut-map-viewport">`-обёртку всего
 * содержимого SVG. Координаты указателя (px) → координаты viewBox через
 * `svg.getScreenCTM()` (axis-aligned scale+translate у outermost SVG —
 * инверсия без матриц: `(client − e)/a`). Без CTM (jsdom) хук инертен.
 *
 * **Жесты:** 2 пальца — pinch-zoom вокруг центра щипка (+ пан по центру);
 * 1 палец — drag-pan; дабл-тап (2 тапа < 300мс рядом) — сброс. Диапазон
 * масштаба 1×–4× (clampMapScale). Карта read-only → конфликтов нет.
 *
 * **Сброс:** при смене `resetKey` (номер листа в пагере) и при `enabled=false`
 * вид сбрасывается в identity — каждый лист открывается свежим видом.
 *
 * **touch-action:** при `enabled` на SVG ставится `touch-action: none` +
 * слушатели `passive:false` с `preventDefault` при активном жесте — иначе
 * браузер перехватит pinch/скролл. Активен ТОЛЬКО на мобильном (`enabled`,
 * обычно `matchMedia('(max-width:480px)')`); на десктопе инертен.
 */
export interface UseSvgPanZoomOptions {
  /** Внешний `<svg>` — слушатель pointer-событий + источник `getScreenCTM`. */
  svgRef: RefObject<SVGSVGElement | null>;
  /** Внутренний `<g>`-обёртка — на неё пишется `transform`. */
  contentRef: RefObject<SVGGElement | null>;
  /** Активен ли зум/пан (мобильный). На false хук инертен + сброс вида. */
  enabled: boolean;
  /** Смена значения (номер листа) сбрасывает вид в identity. */
  resetKey: number;
}

const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_DIST = 30; // px между тапами
const TAP_MOVE_TOLERANCE = 10; // px — больше = это уже pan, не тап

export function useSvgPanZoom({
  svgRef,
  contentRef,
  enabled,
  resetKey,
}: UseSvgPanZoomOptions): void {
  const viewRef = useRef<ViewTransform>(IDENTITY_VIEW);
  const pointersRef = useRef<Map<number, Point>>(new Map());
  const rafRef = useRef<number | null>(null);

  // Pinch-базлайн (2 пальца).
  const lastDistRef = useRef<number>(0);
  const lastMidVbRef = useRef<Point | null>(null);
  // Pan-базлайн (1 палец) в координатах viewBox.
  const lastPanVbRef = useRef<Point | null>(null);
  // Кандидат в тап (1 палец, без значимого движения) для дабл-тапа.
  const tapStartRef = useRef<Point | null>(null);
  const lastTapRef = useRef<{ t: number; x: number; y: number } | null>(null);

  // Сброс вида в identity при смене листа / выключении.
  useEffect(() => {
    viewRef.current = IDENTITY_VIEW;
    const content = contentRef.current;
    if (content) content.setAttribute('transform', toTransformString(IDENTITY_VIEW));
  }, [resetKey, enabled, contentRef]);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    // Десктоп / выключено — хук инертен, вид сброшен, touch-action не трогаем.
    if (!enabled) {
      svg.style.removeProperty('touch-action');
      svg.style.removeProperty('cursor');
      viewRef.current = IDENTITY_VIEW;
      return;
    }

    svg.style.touchAction = 'none';
    svg.style.cursor = 'grab';

    const pointers = pointersRef.current;

    /** px указателя → координаты viewBox (инверсия axis-aligned CTM). */
    const toViewBox = (clientX: number, clientY: number): Point | null => {
      const ctm = svg.getScreenCTM?.();
      if (!ctm || !ctm.a || !ctm.d) return null;
      return { x: (clientX - ctm.e) / ctm.a, y: (clientY - ctm.f) / ctm.d };
    };

    const writeTransform = () => {
      rafRef.current = null;
      const content = contentRef.current;
      if (content) content.setAttribute('transform', toTransformString(viewRef.current));
    };

    const scheduleWrite = () => {
      if (rafRef.current != null) return;
      const raf =
        typeof window.requestAnimationFrame === 'function'
          ? window.requestAnimationFrame
          : (cb: FrameRequestCallback) => window.setTimeout(() => cb(performance.now()), 16);
      rafRef.current = raf(writeTransform);
    };

    const onPointerDown = (e: PointerEvent) => {
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 1) {
        tapStartRef.current = { x: e.clientX, y: e.clientY };
        lastPanVbRef.current = toViewBox(e.clientX, e.clientY);
      } else if (pointers.size === 2) {
        tapStartRef.current = null; // два пальца — не тап
        const pts = [...pointers.values()];
        lastDistRef.current = distance(pts[0]!, pts[1]!);
        const mid = midpoint(pts[0]!, pts[1]!);
        lastMidVbRef.current = toViewBox(mid.x, mid.y);
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (pointers.size >= 2) {
        // Pinch (2 пальца): зум вокруг центра + пан по сдвигу центра.
        const pts = [...pointers.values()];
        const curDist = distance(pts[0]!, pts[1]!);
        const midClient = midpoint(pts[0]!, pts[1]!);
        const curMidVb = toViewBox(midClient.x, midClient.y);
        if (curMidVb && lastDistRef.current > 0) {
          let v = viewRef.current;
          if (lastMidVbRef.current) {
            v = panBy(v, curMidVb.x - lastMidVbRef.current.x, curMidVb.y - lastMidVbRef.current.y);
          }
          v = zoomAround(v, curDist / lastDistRef.current, curMidVb.x, curMidVb.y);
          viewRef.current = v;
        }
        lastDistRef.current = curDist;
        lastMidVbRef.current = curMidVb;
        e.preventDefault();
        scheduleWrite();
        return;
      }

      // 1 палец: drag-pan. Значимое движение снимает кандидата в тап.
      const start = tapStartRef.current;
      if (start && Math.hypot(e.clientX - start.x, e.clientY - start.y) > TAP_MOVE_TOLERANCE) {
        tapStartRef.current = null;
      }
      const curVb = toViewBox(e.clientX, e.clientY);
      if (curVb && lastPanVbRef.current) {
        viewRef.current = panBy(
          viewRef.current,
          curVb.x - lastPanVbRef.current.x,
          curVb.y - lastPanVbRef.current.y,
        );
        lastPanVbRef.current = curVb;
        e.preventDefault();
        scheduleWrite();
      }
    };

    const endPointer = (e: PointerEvent) => {
      // Дабл-тап (только если палец был один и не двигался) → сброс вида.
      const wasTap = pointers.size === 1 && tapStartRef.current != null;
      if (wasTap) {
        const now = performance.now();
        const prev = lastTapRef.current;
        if (
          prev &&
          now - prev.t < DOUBLE_TAP_MS &&
          Math.hypot(e.clientX - prev.x, e.clientY - prev.y) < DOUBLE_TAP_DIST
        ) {
          viewRef.current = IDENTITY_VIEW;
          lastTapRef.current = null;
          e.preventDefault();
          scheduleWrite();
        } else {
          lastTapRef.current = { t: now, x: e.clientX, y: e.clientY };
        }
      }

      pointers.delete(e.pointerId);
      lastDistRef.current = 0; // pinch завершён
      if (pointers.size === 1) {
        // Остался 1 палец — продолжаем pan от его текущей позиции.
        const [pt] = [...pointers.values()];
        lastPanVbRef.current = pt ? toViewBox(pt.x, pt.y) : null;
        lastMidVbRef.current = null;
      } else if (pointers.size === 0) {
        lastPanVbRef.current = null;
        lastMidVbRef.current = null;
      }
    };

    svg.addEventListener('pointerdown', onPointerDown);
    svg.addEventListener('pointermove', onPointerMove, { passive: false });
    svg.addEventListener('pointerup', endPointer);
    svg.addEventListener('pointercancel', endPointer);

    return () => {
      svg.removeEventListener('pointerdown', onPointerDown);
      svg.removeEventListener('pointermove', onPointerMove);
      svg.removeEventListener('pointerup', endPointer);
      svg.removeEventListener('pointercancel', endPointer);
      svg.style.removeProperty('touch-action');
      svg.style.removeProperty('cursor');
      if (rafRef.current != null && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = null;
      pointers.clear();
    };
  }, [enabled, svgRef, contentRef]);
}
